import { createFileRoute, Link } from '@tanstack/react-router'

import { useDeleteCustomerGroup } from '@/api/api-mutations'
import { preloadGetCustomerGroup, useGetCustomer, useGetCustomerGroup } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/_general/customer-groups/$customerGroupUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<CustomerGroupDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { customerGroupUuid } }) => {
		await preloadGetCustomerGroup(context, { path: { customerGroupUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.CustomerGroups.Detail.Title()
	}
})

function CustomerGroupDetail() {
	const { L } = useI18n()
	const { customerGroupUuid } = Route.useParams()
	const navigate = Route.useNavigate()

	const group = useGetCustomerGroup({ customerGroupUuid })
	const deleteCustomerGroup = useDeleteCustomerGroup()
	const prompt = usePrompt()

	const handleDelete = async () => {
		const res = await prompt({
			title: L.CustomerGroups.Delete.Title(),
			description: L.CustomerGroups.Delete.Description({ name: group.data?.name ?? '' }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!res) {
			return
		}
		const result = await deleteCustomerGroup({ path: { customerGroupUuid } })
		if (result.isSuccess) {
			toast.success(L.CustomerGroups.Delete.SuccessToast({ name: group.data?.name ?? '' }))
			navigate({ to: '/customer-groups', replace: true })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container className='divide-y'>
				<ContainerHeader
					heading={group.data?.name || L.CustomerGroups.Detail.Title()}
					rightSide={
						<Link from={Route.fullPath} to='edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									icon: <Trash />,
									onClick: handleDelete
								}
							]
						}
					]}
				/>
				<SectionRow
					title={L.CustomerGroups.Field.Description()}
					value={group.data?.description || L.General.NotSet()}
				/>
				<SectionRow
					title={L.CustomerGroups.Field.ExternalId()}
					value={group.data?.externalId || L.General.NotSet()}
				/>
				<SectionRow
					title={L.CustomerGroups.Column.MemberCount()}
					value={group.data != null ? String(group.data.customerCount) : L.General.NotSet()}
				/>
			</Container>

			<Container className='divide-y'>
				<ContainerHeader heading={L.CustomerGroups.Members()} />
				{group.data?.customerUuids && group.data.customerUuids.length > 0 ? (
					group.data.customerUuids.map(uuid => <CustomerRow key={uuid} uuid={uuid} />)
				) : (
					<div className='px-6 py-4'>
						<Text size='small' leading='compact' className='text-ui-fg-muted'>
							{L.General.NoResultsTitle()}
						</Text>
					</div>
				)}
			</Container>
		</SingleColumnPage>
	)
}

function CustomerRow({ uuid }: { uuid: string }) {
	const customer = useGetCustomer({ customerUuid: uuid })
	const c = customer.data
	const label = c ? [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email || c.phone || uuid : uuid
	return (
		<Link to='/customers/$customerUuid' params={{ customerUuid: uuid }} className='flex flex-col px-6 py-3'>
			<Text size='small' weight='plus'>
				{label}
			</Text>
		</Link>
	)
}
